import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const TicketDownloadModal = ({ isOpen, onClose, booking = null, mode = 'search' }) => {
  const [searchType, setSearchType] = useState('phone');
  const [searchValue, setSearchValue] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);

  const searchTickets = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/my-tickets`, {
        params: { [searchType]: searchValue }
      });
      setTickets(response.data.bookings);
    } catch (error) {
      setError(error.response?.data?.message || 'No tickets found');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadTicket = async (ticket) => {
    setDownloadLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/pdf/generate-pdf`, {
        bookingId: ticket.bookingId
      }, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticket.bookingId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('Failed to download ticket. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  if (!isOpen) return null;

  // Direct booking display mode
  if (mode === 'display' && booking) {
    const qrData = `TICKET:${booking.bookingId}|VENUE:${booking.ticketTypeId?.name}|AMOUNT:${booking.totalAmount}|DATE:${booking.visitDate}`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '0',
          maxWidth: '400px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* Close Button */}
          <div style={{ position: 'absolute', top: '10px', right: '15px', zIndex: 10 }}>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(0,0,0,0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>

          {/* Full Ticket Preview */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            overflow: 'hidden',
            fontFamily: 'Arial, sans-serif'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: 'white',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
                {booking.ticketTypeId?.name || 'Site Name'}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                Welcome to {booking.ticketTypeId?.name || 'Site Name'}
              </div>
            </div>

            {/* Ticket Content */}
            <div style={{ padding: '20px' }}>
              {/* QR Section */}
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                padding: '15px',
                background: 'linear-gradient(135deg, #e3f2fd, #f3e5f5)',
                borderRadius: '12px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px' }}>
                  🎫 Your E-Ticket
                </div>
                <img src={qrCodeUrl} alt="QR Code" style={{
                  width: '120px',
                  height: '120px',
                  margin: '0 auto 15px',
                  border: '3px solid white',
                  borderRadius: '8px'
                }} />
                
                {/* Pass Photo for Pass Tickets */}
                {booking.ticketTypeId?.category === 'pass_ticket' && booking.documents?.passportPhoto && (
                  <div style={{ margin: '10px 0' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Pass Holder Photo</div>
                    <img 
                      src={booking.documents.passportPhoto} 
                      alt="Pass Photo"
                      style={{ 
                        width: '60px', 
                        height: '60px', 
                        objectFit: 'cover', 
                        borderRadius: '8px', 
                        border: '2px solid #e5e7eb' 
                      }}
                    />
                  </div>
                )}
                
                {/* Ticket Details */}
                <div style={{
                  background: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '12px',
                  textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#666' }}>Ticket ID:</span>
                    <span style={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '11px', background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>
                      {booking.bookingId}
                    </span>
                  </div>
                  
                  <div style={{
                    padding: '6px',
                    borderRadius: '6px',
                    margin: '8px 0',
                    fontSize: '11px',
                    background: '#fff3cd',
                    borderLeft: '3px solid #ffc107'
                  }}>
                    ⚠️ <strong>Valid for single entry only</strong> • Show this QR code at entrance
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#666' }}>Entry Type:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {booking.ticketTypeId?.category?.replace('_', ' ').toUpperCase() || 'ENTRY TICKET'}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#666' }}>Customer:</span>
                    <span style={{ fontWeight: 'bold' }}>{booking.customerDetails?.name || 'Guest'}</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#666' }}>Quantity:</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {booking.adults && booking.children ? 
                        `Adult ${booking.adults}, Child ${booking.children} (Total ${booking.quantity})` :
                        booking.quantity || 1
                      }
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#666' }}>Amount:</span>
                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>₹{booking.totalAmount}</span>
                  </div>
                  
                  {booking.ticketTypeId?.category === 'pass_ticket' ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                        <span style={{ color: '#666' }}>Pass Valid From:</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {(() => {
                            try {
                              const date = booking.createdAt.includes('T') ? 
                                new Date(booking.createdAt) : 
                                new Date(booking.createdAt + 'T00:00:00');
                              return date.toLocaleDateString('en-IN', { 
                                weekday: 'short', 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              });
                            } catch (e) {
                              return 'Date not available';
                            }
                          })()} 
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                        <span style={{ color: '#666' }}>Pass Valid Till:</span>
                        <span style={{ fontWeight: 'bold', color: '#dc3545' }}>
                          {(() => {
                            try {
                              const startDate = new Date(booking.createdAt);
                              const validityDays = booking.ticketTypeId?.validity?.duration || 30;
                              const expiryDate = new Date(startDate);
                              expiryDate.setDate(expiryDate.getDate() + validityDays);
                              return expiryDate.toLocaleDateString('en-IN', { 
                                weekday: 'short', 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              });
                            } catch (e) {
                              return 'Date not available';
                            }
                          })()} 
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                        <span style={{ color: '#666' }}>Pass Type:</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {booking.ticketTypeId?.passTicketSettings?.passType === 'limited' 
                            ? `Limited (${booking.ticketTypeId?.passTicketSettings?.visitLimit || 10} visits)`
                            : 'Unlimited Visits'
                          }
                        </span>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#666' }}>Visit Date:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        {(() => {
                          try {
                            const date = booking.visitDate.includes('T') ? 
                              new Date(booking.visitDate) : 
                              new Date(booking.visitDate + 'T00:00:00');
                            return date.toLocaleDateString('en-IN', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            });
                          } catch (e) {
                            return booking.visitDate || 'Date not available';
                          }
                        })()} 
                      </span>
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                    <span style={{ color: '#666' }}>Entry Time:</span>
                    <span style={{ fontWeight: 'bold', color: '#dc3545' }}>9:00 AM - 6:00 PM</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #ddd',
                    paddingTop: '10px',
                    marginTop: '10px',
                    fontSize: '13px'
                  }}>
                    <span style={{ color: '#666' }}>Status:</span>
                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>✅ Confirmed</span>
                  </div>
                </div>

                {/* Function Hall Details */}
                {booking.ticketTypeId?.category === 'function_hall_ticket' && booking.categorySpecificData && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                    padding: '15px',
                    borderRadius: '12px',
                    marginTop: '15px',
                    border: '2px solid #f59e0b'
                  }}>
                    <div style={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      marginBottom: '12px',
                      color: '#92400e',
                      fontSize: '14px',
                      borderBottom: '2px solid #f59e0b',
                      paddingBottom: '8px'
                    }}>
                      🏛️ FUNCTION HALL BOOKING
                    </div>
                    
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '8px',
                        marginBottom: '8px',
                        fontSize: '10px',
                        textAlign: 'center'
                      }}>
                        <div><strong>Hall Capacity:</strong><br/>100 people</div>
                        <div><strong>Duration:</strong><br/>{booking.categorySpecificData?.duration || 4} hours</div>
                        <div><strong>Base Price:</strong><br/>₹{booking.ticketTypeId?.price || 0}</div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '10px' }}>
                        {booking.categorySpecificData.eventType && (
                          <div><strong>Event Type:</strong> {booking.categorySpecificData.eventType.charAt(0).toUpperCase() + booking.categorySpecificData.eventType.slice(1)}</div>
                        )}
                        {booking.categorySpecificData.expectedGuests && (
                          <div><strong>Expected Guests:</strong> {booking.categorySpecificData.expectedGuests} people</div>
                        )}
                      </div>
                    </div>

                    {/* Selected Services */}
                    {(() => {
                      let servicesList = [];
                      
                      // Check selectedServices array first
                      if (booking.selectedServices && Array.isArray(booking.selectedServices) && booking.selectedServices.length > 0) {
                        servicesList = booking.selectedServices;
                      }
                      // Check categorySpecificData.selectedServices object
                      else if (booking.categorySpecificData?.selectedServices) {
                        servicesList = Object.entries(booking.categorySpecificData.selectedServices)
                          .filter(([_, isSelected]) => isSelected)
                          .map(([name]) => ({ name, price: 0 })); // Price will be calculated from total
                      }
                      
                      if (servicesList.length === 0) return null;
                      
                      const basePrice = booking.ticketTypeId?.price || 0;
                      const servicesTotal = booking.totalAmount - basePrice;
                      
                      return (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{
                            fontWeight: 'bold',
                            marginBottom: '8px',
                            color: '#92400e',
                            fontSize: '12px',
                            textAlign: 'center'
                          }}>
                            🛠️ Selected Services:
                          </div>
                          
                          {servicesList.map((service, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              background: 'rgba(255, 255, 255, 0.9)',
                              padding: '6px 10px',
                              borderRadius: '4px',
                              marginBottom: '3px',
                              fontSize: '10px',
                              border: '1px solid rgba(34, 197, 94, 0.2)'
                            }}>
                              <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                                {service.name.replace(/_/g, ' ')}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '9px', color: '#666' }}>Qty: 1</span>
                                <span style={{ fontWeight: 'bold', color: '#16a34a', fontSize: '10px' }}>
                                  ₹{service.price || 0}
                                </span>
                              </div>
                            </div>
                          ))}
                          
                          <div style={{
                            textAlign: 'center',
                            marginTop: '8px',
                            padding: '6px',
                            background: 'rgba(34, 197, 94, 0.1)',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#15803d'
                          }}>
                            Total Amount: ₹{booking.totalAmount}<br/>
                            <span style={{ fontSize: '9px', fontWeight: 'normal' }}>Hall Booking + Selected Services</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Important Instructions */}
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(239, 68, 68, 0.2)'
                    }}>
                      <div style={{
                        fontWeight: 'bold',
                        marginBottom: '6px',
                        color: '#dc2626',
                        fontSize: '10px',
                        textAlign: 'center'
                      }}>
                        ⚠️ IMPORTANT INSTRUCTIONS
                      </div>
                      <div style={{
                        fontSize: '9px',
                        lineHeight: '1.4',
                        color: '#374151',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '2px'
                      }}>
                        <div>🕐 Hall available from booked time</div>
                        <div>🧹 Maintain cleanliness</div>
                        <div>🔊 Follow noise regulations</div>
                        <div>🚫 No smoking/alcohol policy</div>
                        <div>💰 Damages charged separately</div>
                        <div>📞 Contact for additional needs</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              textAlign: 'center',
              fontSize: '10px',
              color: '#666',
              borderTop: '1px solid #e9ecef'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>{booking.ticketTypeId?.name || 'Site Name'}</strong>
              </div>
              <div style={{ marginBottom: '8px' }}>Ralaba, Bramhapur</div>
              <div style={{ marginTop: '5px' }}>📞 +91 9876543210</div>
              <div style={{ marginTop: '5px', fontSize: '9px' }}>
                🎟️ Keep safe • Non-transferable • No refunds
              </div>
              <div style={{ marginTop: '5px', fontStyle: 'italic' }}>
                Thank you for visiting! Have a wonderful day!
              </div>
            </div>

            {/* Download Button */}
            <div style={{ padding: '20px' }}>
              <button
                onClick={() => downloadTicket(booking)}
                disabled={downloadLoading}
                style={{
                  width: '100%',
                  background: downloadLoading ? '#a0aec0' : 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: downloadLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {downloadLoading ? '📥 Generating PDF...' : '📥 Download Ticket PDF'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Search mode
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '30px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#2d3748' }}>📱 My Tickets</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#718096'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={searchTickets} style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Search by:
            </label>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="phone"
                  checked={searchType === 'phone'}
                  onChange={(e) => setSearchType(e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                📞 Phone
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="email"
                  checked={searchType === 'email'}
                  onChange={(e) => setSearchType(e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                📧 Email
              </label>
            </div>
          </div>

          <input
            type={searchType === 'email' ? 'email' : 'tel'}
            placeholder={searchType === 'email' ? 'Enter your email address' : 'Enter your 10-digit phone number'}
            value={searchValue}
            onChange={(e) => {
              if (searchType === 'phone') {
                const phone = e.target.value.replace(/[^0-9]/g, '');
                if (phone.length <= 10) {
                  setSearchValue(phone);
                }
              } else {
                setSearchValue(e.target.value);
              }
            }}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '16px',
              marginBottom: '16px'
            }}
            pattern={searchType === 'phone' ? '[0-9]{10}' : undefined}
            minLength={searchType === 'phone' ? '10' : undefined}
            maxLength={searchType === 'phone' ? '10' : undefined}
            required
          />

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: loading ? '#a0aec0' : 'linear-gradient(135deg, #48bb78, #38a169)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '🔍 Searching...' : '🔍 Find My Tickets'}
          </button>
        </form>

        {error && (
          <div style={{
            background: '#fed7d7',
            color: '#742a2a',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {tickets.length > 0 && (
          <div>
            <h3 style={{ marginBottom: '16px', color: '#2d3748' }}>Your Tickets ({tickets.length})</h3>
            {tickets.map(ticket => (
              <div key={ticket._id} style={{
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
                background: '#f8fafc'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px', color: '#2d3748' }}>{ticket.ticketTypeId?.name}</h4>
                    <p style={{ margin: '0 0 4px', fontSize: '14px', color: '#718096' }}>
                      Booking ID: {ticket.bookingId}
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', color: '#718096' }}>
                      Visit: {new Date(ticket.visitDate + 'T00:00:00').toLocaleDateString()}
                    </p>
                  </div>
                  <span style={{
                    background: ticket.status === 'confirmed' ? '#48bb78' : 
                               ticket.status === 'pending' ? '#ed8936' : '#e53e3e',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {ticket.status.toUpperCase()}
                  </span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: '#2d3748' }}>
                    ₹{ticket.totalAmount} • Qty: {ticket.quantity}
                  </span>
                  <button
                    onClick={() => downloadTicket(ticket)}
                    disabled={downloadLoading}
                    style={{
                      background: downloadLoading ? '#a0aec0' : 'linear-gradient(135deg, #667eea, #764ba2)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: downloadLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {downloadLoading ? '📥 Generating...' : '📥 Download PDF'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDownloadModal;